/**
 * t574u: the connection indicator's state machine, shared by the web (sidebar Wi-Fi icon, phone banners) and the iOS
 * app (offline banners). Pure and clock-injected so it can be unit tested.
 *
 * Why it exists: the old indicator flipped to "Reconnecting…" on any single channel error and only cleared on the next
 * successful join, so a topic the server refuses (or a channel closed while errored) left it red for good while the
 * app worked fine. Here the display is worked out from what is true now:
 *  - a problem (device offline, live updates down) only shows once it has lasted `graceMs` (brief blips never show);
 *  - any sign of life (an API response, a realtime message or heartbeat reply) clears it straight away and restarts
 *    the clock, because the app is demonstrably talking to the server;
 *  - a successful contact also overrides a stale "offline" from the device (navigator.onLine / NetInfo false negatives).
 */
export type ConnectionStatus = 'online' | 'reconnecting' | 'offline';

/** How long a problem must last before the indicator shows it. */
export const CONNECTION_GRACE_MS = 8_000;

/** Hover / accessibility wording (en-GB, plain and calm). */
export const CONNECTION_COPY: Record<ConnectionStatus, string> = {
  online: 'Online',
  reconnecting: 'Reconnecting…',
  offline: "You're offline. Changes will sync when you're back online.",
};

export interface ConnectionMonitorOptions {
  graceMs?: number;
  now?: () => number;
  /** Starting device signal (navigator.onLine / NetInfo). Default true. */
  deviceOnline?: boolean;
}

export class ConnectionMonitor {
  readonly graceMs: number;
  private readonly now: () => number;
  /** When the device said it went offline (null while it says online). */
  private offlineSince: number | null;
  /** When live updates went down (null while up, or when this client doesn't use them). */
  private realtimeDownSince: number | null = null;
  /** Last proof the server is reachable. */
  private lastContact = -Infinity;
  private listeners = new Set<(s: ConnectionStatus) => void>();
  private last: ConnectionStatus;
  private lastDevice: boolean;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(o: ConnectionMonitorOptions = {}) {
    this.graceMs = o.graceMs ?? CONNECTION_GRACE_MS;
    this.now = o.now ?? (() => Date.now());
    this.offlineSince = o.deviceOnline === false ? this.now() : null;
    this.last = this.status();
    this.lastDevice = this.isDeviceOnline();
  }

  /** The device's own signal changed. */
  setDeviceOnline(online: boolean) {
    if (online) this.offlineSince = null;
    else if (this.offlineSince === null) this.offlineSince = this.now();
    this.changed();
  }

  /** Live updates (the realtime socket and its channels) are up or down. Repeated calls keep the first "down" time. */
  setRealtimeUp(up: boolean) {
    if (up) this.realtimeDownSince = null;
    else if (this.realtimeDownSince === null) this.realtimeDownSince = this.now();
    this.changed();
  }

  /** The server answered (any API response, a realtime message, a heartbeat reply). */
  contact() {
    this.lastContact = this.now();
    // We just heard from the server, so the device is not offline whatever it says.
    this.offlineSince = null;
    this.changed();
  }

  isRealtimeDown() { return this.realtimeDownSince !== null; }
  /** The device's own (raw) signal, after any contact override. */
  isDeviceOnline() { return this.offlineSince === null; }

  /** What the indicator should show at `at` (default now). */
  status(at = this.now()): ConnectionStatus {
    if (this.offlineSince !== null && at - Math.max(this.offlineSince, this.lastContact) >= this.graceMs) return 'offline';
    if (this.realtimeDownSince !== null && at - Math.max(this.realtimeDownSince, this.lastContact) >= this.graceMs) return 'reconnecting';
    return 'online';
  }

  /** When the shown status could next change on its own (a grace period running out), or null. */
  nextChangeAt(at = this.now()): number | null {
    const due: number[] = [];
    if (this.offlineSince !== null) due.push(Math.max(this.offlineSince, this.lastContact) + this.graceMs);
    if (this.realtimeDownSince !== null) due.push(Math.max(this.realtimeDownSince, this.lastContact) + this.graceMs);
    const next = due.filter(t => t > at).sort((a, b) => a - b)[0];
    return next ?? null;
  }

  /** Called when the shown status, or the device signal (isDeviceOnline), changes. */
  subscribe(fn: (s: ConnectionStatus) => void) { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  getStatus = () => this.last;

  /** Re-work out the status, tell listeners if it changed, and arm a timer for the next grace expiry. */
  changed() {
    const at = this.now();
    const s = this.status(at);
    const deviceOnline = this.isDeviceOnline();
    if (s !== this.last || deviceOnline !== this.lastDevice) { this.last = s; this.lastDevice = deviceOnline; this.listeners.forEach(l => l(s)); }
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    const next = this.nextChangeAt(at);
    if (next !== null) this.timer = setTimeout(() => { this.timer = null; this.changed(); }, Math.max(0, next - at) + 5);
  }

  dispose() { if (this.timer) clearTimeout(this.timer); this.timer = null; this.listeners.clear(); }
}

/**
 * A join refused by the server's topic rules (RLS: "You do not have permissions to read from this Channel topic"), not a
 * transport problem, so it says nothing about the connection and retrying straight away won't help. An expired token
 * ("Token has expired") is not a refusal: the client refreshes the token and the rejoin works.
 */
export function isTopicRefusal(err: unknown): boolean {
  let msg = '';
  if (err instanceof Error) {
    const cause = (err as { cause?: unknown }).cause;
    msg = `${err.message} ${cause ? safeJson(cause) : ''}`;
  } else msg = typeof err === 'string' ? err : safeJson(err);
  return /permission/i.test(msg) && !/expired/i.test(msg);
}

function safeJson(v: unknown): string {
  try { return JSON.stringify(v) ?? ''; } catch { return String(v); }
}

export type TopicState = 'joining' | 'joined' | 'errored' | 'refused';

/**
 * t574u: per-topic channel health. Live updates count as down only while a topic that should work has errored (or the
 * socket's heartbeat failed); topics the server refuses, and topics no longer open, don't count. Worked out from the
 * current set each time, so closing an errored channel can't leave the indicator stuck.
 */
export class TopicHealth {
  private topics = new Map<string, TopicState>();
  private socketDown = false;

  set(topic: string, state: TopicState) { this.topics.set(topic, state); if (state === 'joined') this.socketDown = false; }
  remove(topic: string) { this.topics.delete(topic); }
  get(topic: string): TopicState | undefined { return this.topics.get(topic); }
  /** The socket's own heartbeat: 'ok' proves it's up, 'timeout' / 'error' that it's dropping. */
  heartbeat(ok: boolean) { this.socketDown = !ok; }
  isDown(): boolean {
    if (this.socketDown && this.topics.size > 0) return true;
    for (const s of this.topics.values()) if (s === 'errored') return true;
    return false;
  }
  refused(): string[] { return [...this.topics].filter(([, s]) => s === 'refused').map(([t]) => t); }
}
